import { Component, Inject, OnInit } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { SimpleModalComponent } from "ngx-simple-modal";
import { CpeService } from "../device/cpes/cpe.service";
import { InventoryService } from "@nms-ng2/app/shared/services/inventory/inventory.service";
import {
    ANGULARJS_TRANSLATE,
    NMS_LICENSES,
    APPLICATION_BRAND_ICONS,
    LICENSE_SERVICE,
    ANGULARJS_ROOTSCOPE,
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";

declare let angular: any;
declare let _: any;

export interface AboutModalModel {
    title: string;
    iconUrl: string;
    version: string;
    licenseType: string;
    endDate: number;
    deviceCount: any;
    maxDevices: any;
    cpeCountDatacom: number;
    cpeCountMultivendor: number;
    maxCpesDatacom: number;
    maxCpesMultivendor: number;
    brandCode: number;
    productName: string;
    possibleFeatures: string[];
    enabledFeatures: string[];
    installationType: string;
}

const versionTypeNames = {
    ENTERPRISE_LITE: "Enterprise Lite",
    ENTERPRISE_FULL: "Enterprise"
};

@Component({
    selector: "about-modal",
    templateUrl: "./about-modal.component.html",
    styleUrls: ["./about-modal.component.scss"],
})
export class AboutModalComponent extends SimpleModalComponent<AboutModalModel, any> implements AboutModalModel, OnInit {
    title: string;
    iconUrl: string;
    version: string;
    licenseType: string;
    endDate: number;
    deviceCount: any;
    maxDevices: any;
    cpeCountDatacom: number;
    cpeCountMultivendor: number;
    maxCpesDatacom: number;
    maxCpesMultivendor: number;
    brandCode: number;
    productName: string;
    possibleFeatures: string[];
    enabledFeatures: string[];
    installationType: string;
    versionDescription: string;
    hasUpdate: boolean;
    versionAvailable: string = "";
    isDownloading: boolean = false;

    constructor(private readonly http: HttpClient,
            private readonly cpeService: CpeService,
            private readonly inventoryService: InventoryService,
            @Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
            @Inject(NMS_LICENSES) private readonly LICENSES: any,
            @Inject(APPLICATION_BRAND_ICONS) private readonly ApplicationIcons: any,
            @Inject(LICENSE_SERVICE) private readonly LicenseService: any,
            @Inject(ANGULARJS_ROOTSCOPE) private readonly $rootScope: any) {
        super();
        let license = LicenseService.getLicense();
        this.title = translate.instant("modals.about.title");
        this.iconUrl = ApplicationIcons[license.product.brandCode];
        this.version = license.product.swVersion;
        this.licenseType = license.type;
        this.endDate = license.endDate;
        this.maxDevices = license.nes;
        this.maxCpesDatacom = license.maxCpesDatacom;
        this.maxCpesMultivendor = license.maxCpesMultivendor;
        this.brandCode = license.product.brandCode;
        this.productName = license.product.productName;
        this.possibleFeatures = _.map(_.omit(LICENSES, "notRequired"));
        this.enabledFeatures = license.features;
        this.installationType = license.product.installationType;
        this.versionDescription = this.getVersionDescription();
    }

    ngOnInit() {
        this.cpeService.getCpeCount().subscribe((data) => {
            this.cpeCountDatacom = data.cpesDatacom;
            this.cpeCountMultivendor = data.cpesMultivendor;
        });

        this.inventoryService.getDevicesCount().subscribe((data) => {
            this.deviceCount = data;
        });
        this.checkUpdates();
    }

    checkUpdates() {
        this.hasUpdate = !!this.$rootScope.lastNmsVersionAvailable;
        if (this.hasUpdate) {
            this.versionAvailable = this.$rootScope.lastNmsVersionAvailable.version;
        }
    }

    getVersionDescription() {
        if (this.installationType == "LOCAL_ACCESS") {
            return "Local Access";
        }

        return versionTypeNames[this.licenseType];
    }

    getDeviceUsageText() {
        return this.translate
            .instant("modals.about.license.deviceUsage")
            .replace("{0}", this.deviceCount)
            .replace("{1}", this.maxDevices);
    }

    getCpeDatacomUsageText() {
        return this.translate
            .instant("modals.about.license.cpeDatacomUsage")
            .replace("{0}", this.cpeCountDatacom)
            .replace("{1}", this.maxCpesDatacom);
    }

    getCpeMultivendorUsageText() {
        return this.translate
            .instant("modals.about.license.cpeMultivendorUsage")
            .replace("{0}", this.cpeCountMultivendor)
            .replace("{1}", this.maxCpesMultivendor);
    }

    getExpirationText() {
        let locale = this.translate.preferredLanguage().replace("_", "-");
        const dateOptions: Intl.DateTimeFormatOptions = { year: "numeric", month: "long", day: "numeric" };
        let expirationDate = new Date(this.endDate).toLocaleDateString(locale, dateOptions);

        return this.translate.instant("modals.about.license.expiresOn").replace("{0}", expirationDate);
    }

    getDeviceUsage() {
        return (this.deviceCount / this.maxDevices) * 100;
    }

    getCpeDatacomUsage() {
        return (this.cpeCountDatacom / this.maxCpesDatacom) * 100;
    }

    getCpeMultivendorUsage() {
        return (this.cpeCountMultivendor / this.maxCpesMultivendor) * 100;
    }

    getTranslatedFeatureName(friendlyName: string): string {
        const keyName = friendlyName.replace(/ /g, ".").toLowerCase();
        return this.translate.instant(`modals.about.license.${keyName}`);
    }

    downloadAvailableVersion() {
        window.open(this.$rootScope.lastNmsVersionAvailable.downloadLink, "_blank");
        this.isDownloading = true;
        setTimeout(() => {
            this.isDownloading = false;
        }, 500);
    }

    getVersionUpdateLabel(): string {
        return this.translate.instant("modals.license.update.available.label").replace("{0}", this.versionAvailable);
    }
}
